import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { AddedToCartModal } from '@/components/cart/added-to-cart-modal';
import { 
  Heart, 
  ChevronLeft, 
  Plus, 
  Minus, 
  ShoppingCart,
  Check,
  X,
  Package,
  Shield,
  Truck,
  AlertCircle,
  Star,
  Share2,
  Eye,
  Zap
} from 'lucide-react';
import { Product, SelectedVariation } from '@/types';
import { useStoreInfo } from '@/lib/store';
import { useStore } from '@/store';
import { formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';
import { ProductDetailSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';

export function ModernProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariations, setSelectedVariations] = useState<SelectedVariation[]>([]);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  
  const addToCart = useStore((state) => state.addToCart);
  const wishlist = useStore((state) => state.wishlist);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const { storeInfo, openLockNotice } = useStoreInfo();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          const foundProduct = data.products.find((p: Product) => p.id === parseInt(id!));
          if (foundProduct) {
            setProduct(foundProduct);
            
            if (foundProduct.variations && foundProduct.variations.length > 0) {
              const variationNames = foundProduct.variations.map((v: any) => v.variation);
              const uniqueNames = new Set(variationNames);
              
              if (variationNames.length !== uniqueNames.size) {
                const seenNames = new Set<string>();
                const uniqueVariations = foundProduct.variations.filter((v: any) => {
                  if (seenNames.has(v.variation)) {
                    return false;
                  }
                  seenNames.add(v.variation);
                  return true;
                });
                
                const initialVariations = uniqueVariations.map((v: any) => ({
                  variation: v.variation,
                  quantity: 0,
                  v_id: v.v_id
                }));
                setSelectedVariations(initialVariations);
              } else {
                const initialVariations = foundProduct.variations.map((v: any) => ({
                  variation: v.variation,
                  quantity: 0,
                  v_id: v.v_id
                }));
                setSelectedVariations(initialVariations);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (storeInfo?.site_lock === 1) {
      openLockNotice();
      return;
    }
    if (isAdding) return; // prevent multiple rapid clicks
    setIsAdding(true);
    if (!product) return;

    const hasVariations = product.variations && product.variations.length > 0;
    const hasRealVariations = hasVariations && product.variations!.some(v => v.variation.toLowerCase() !== 'nil');
    
    if (hasRealVariations) {
      const activeVariations = selectedVariations.filter(v => v.quantity > 0);
      if (activeVariations.length === 0) {
        toast.error('Please select at least one variation', {
          icon: <AlertCircle className="h-5 w-5" />,
          duration: 3000,
        });
        return;
      }
      
      addToCart({
        ...product,
        quantity: 0,
        selectedVariations: activeVariations
      });
    } else {
      addToCart({
        ...product,
        quantity,
        selectedVariations: []
      });
    }
    // Show a prominent toast so users notice the add action
    toast.success('Added to cart', {
      duration: 3000
    });

    // Keep mobile modal for small screens, but disable rapid repeats
    setShowAddedToCart(true);
    setTimeout(() => setShowAddedToCart(false), 3000);

    // Re-enable button after short delay
    setTimeout(() => setIsAdding(false), 1500);
  };

  const updateVariationQuantity = (variationName: string, change: number) => {
    setSelectedVariations(prev =>
      prev.map(v =>
        v.variation === variationName
          ? { ...v, quantity: Math.max(0, v.quantity + change) }
          : v
      )
    );
  };

  const isInWishlist = product ? wishlist.includes(product.id) : false;

  if (loading) return <ProductDetailSkeleton />;
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const hasVariations = product.variations && product.variations.length > 0;
  const discountInfo = calculateProductDiscount(product, quantity);

  return (
    <>
      <Helmet>
        <title>{product.produt_name} | Bnass Beauty</title>
        <meta name="description" content={product.description || 'Discover premium beauty products and accessories at Bnass Beauty...your beauty is our concern.'} />
      </Helmet>

      <div className="min-h-screen bg-white pb-24 md:pb-0">
        {/* FULL WIDTH HERO SECTION */}
        <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
          {/* Back button - always visible, above layout */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-6">
            <motion.button
              whileHover={{ x: -4 }}
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 text-sm font-medium bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-sm border border-gray-200"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </motion.button>
          </div>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-0 items-stretch min-h-[380px] md:min-h-[520px]">
            {/* === LEFT SIDE: LARGE IMAGE CAROUSEL === */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative flex items-center justify-center bg-gray-50 md:rounded-r-3xl overflow-hidden"
            >
              {product.images && product.images.length > 0 ? (
                <Swiper
                  modules={[Navigation, Pagination, Thumbs, FreeMode]}
                  navigation={{ clickable: true }}
                  pagination={{ clickable: true }}
                  thumbs={{ swiper: thumbsSwiper }}
                  onSwiper={setThumbsSwiper}
                  className="w-full h-full"
                  onSlideChange={(swiper) => setSelectedImageIndex(swiper.activeIndex)}
                >
                  {product.images.map((image, index) => (
                    <SwiperSlide
                      key={index}
                      className="flex items-center justify-center p-4 sm:p-6 bg-gray-50 min-h-[320px] sm:min-h-[420px] md:min-h-[520px] lg:min-h-[600px]"
                    >
                      <img
                        src={image}
                        alt={`${product.produt_name} ${index + 1}`}
                        className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300"
                        loading={index === 0 ? 'eager' : 'lazy'}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <NoImagePlaceholder className="w-full h-full rounded-none" size="lg" />
              )}

              {/* Image Badge */}
              {product.images && product.images.length > 1 && (
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-gray-900">
                  {selectedImageIndex + 1} / {product.images.length}
                </div>
              )}
            </motion.div>

            {/* === RIGHT SIDE: STICKY PRODUCT INFO === */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col overflow-y-auto max-h-screen md:sticky md:top-0 p-6 md:p-8 space-y-6 md:border-l border-gray-200"
            >
              {/* Category Badge */}
              <div>
                <span className="inline-block text-xs font-bold text-brand-600 uppercase tracking-wide mb-3">
                  {product.category || 'Hair Care'} • Premium Collection
                </span>
              </div>

              {/* Title & Price Header */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 leading-tight"
                >
                  {product.produt_name}
                </motion.h1>

                {/* Price & Discount */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-baseline gap-3 mb-4"
                >
                  <div className="text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500">
                    {formatPrice(product.sellingprice)}
                  </div>
                  {discountInfo.hasDiscount && (
                    <div className="text-xl text-gray-400 line-through font-semibold">
                      {formatPrice(parseFloat(product.sellingprice) * 1.2)}
                    </div>
                  )}
                </motion.div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {discountInfo.hasDiscount && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-red-100 text-red-700">
                      <Zap className="h-4 w-4" />
                      {discountInfo.discountType === 'percentage' 
                        ? `${discountInfo.discountAmount}% OFF` 
                        : `Save ${formatPrice(discountInfo.discountAmount)}`}
                    </div>
                  )}
                  {product.postType === "1" && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-purple-100 text-purple-700">
                      Pre-Order
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Features */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Package, label: 'Premium', desc: 'Quality' },
                  { icon: Truck, label: 'Fast', desc: 'Delivery' },
                  { icon: Shield, label: 'Secure', desc: 'Checkout' }
                ].map((feature, idx) => (
                  <motion.div
                    key={feature.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + idx * 0.05 }}
                    className="p-3 bg-gradient-to-br from-brand-50 to-white rounded-xl border border-brand-100 text-center"
                  >
                    <feature.icon className="h-5 w-5 text-brand-600 mx-auto mb-1" />
                    <div className="text-xs font-bold text-gray-900">{feature.label}</div>
                    <div className="text-2xs text-gray-600">{feature.desc}</div>
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="h-0.5 bg-gradient-to-r from-brand-200 via-brand-100 to-gray-50" />

              {/* Variations */}
              {hasVariations && product.variations!.some(v => v.variation.toLowerCase() !== 'nil') && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">Choose Options</h3>
                  <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                    {product.variations!
                      .filter(v => v.variation.toLowerCase() !== 'nil')
                      .sort((a, b) => {
                        const aQty = parseInt((a as any).v_qty || '0');
                        const bQty = parseInt((b as any).v_qty || '0');
                        if (aQty > 0 && bQty === 0) return -1;
                        if (aQty === 0 && bQty > 0) return 1;
                        return 0;
                      })
                      .map((variation, idx) => {
                        const selected = selectedVariations.find(v => v.variation === variation.variation);
                        const varPrice = (variation as any).variation_amount || 0;
                        const displayPrice = varPrice > 0 ? varPrice : product.sellingprice;
                        const availableQty = parseInt((variation as any).v_qty || '0');
                        const isSoldOut = availableQty === 0;
                        
                        return (
                          <motion.div
                            key={variation.v_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className={`p-4 rounded-2xl border-2 transition-all ${
                              isSoldOut
                                ? 'bg-gray-50 border-gray-200 opacity-60'
                                : selected && selected.quantity > 0
                                ? 'bg-gradient-to-br from-brand-50 to-brand-100 border-brand-400 shadow-lg'
                                : 'bg-white border-gray-300 hover:border-brand-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className={`font-bold text-base ${isSoldOut ? 'text-gray-500' : 'text-gray-900'}`}>
                                    {variation.variation}
                                  </p>
                                  {isSoldOut && (
                                    <span className="text-xs px-2 py-0.5 bg-red-200 text-red-700 rounded-full font-bold">Sold Out</span>
                                  )}
                                </div>
                                <p className={`text-lg font-bold ${isSoldOut ? 'text-gray-400' : 'text-brand-600'}`}>
                                  {formatPrice(displayPrice)}
                                </p>
                              </div>
                            </div>
                            {!isSoldOut && (
                              <div className="flex items-center gap-2 bg-white rounded-lg p-1.5 border border-gray-300 w-fit">
                                <button
                                  onClick={() => updateVariationQuantity(variation.variation, -1)}
                                  disabled={!selected || selected.quantity === 0}
                                  className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-brand-100 disabled:opacity-50 flex items-center justify-center"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="font-bold w-6 text-center text-gray-900">{selected?.quantity || 0}</span>
                                <button
                                  onClick={() => updateVariationQuantity(variation.variation, 1)}
                                  disabled={selected && selected.quantity >= availableQty}
                                  className="w-7 h-7 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 flex items-center justify-center text-white"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Quantity Selector (Non-variation) */}
              {(!hasVariations || product.variations!.every(v => v.variation.toLowerCase() === 'nil')) && (
                <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-5 border-2 border-brand-200">
                  <h3 className="font-bold text-gray-900 mb-4">Quantity</h3>
                  <div className="flex items-center gap-3 bg-white border border-brand-300 rounded-xl p-2 w-fit">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-brand-100 flex items-center justify-center"
                    >
                      <Minus className="h-5 w-5" />
                    </button>
                    <span className="text-2xl font-bold w-12 text-center text-gray-900">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.totalStock, quantity + 1))}
                      disabled={quantity >= product.totalStock}
                      className="w-9 h-9 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 flex items-center justify-center text-white"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Desktop CTA Buttons */}
              <div className="hidden md:flex gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => toggleWishlist(product.id)}
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-all font-bold text-lg ${
                    isInWishlist
                      ? 'bg-brand-100 border-brand-500 text-brand-600'
                      : 'bg-white border-gray-300 text-gray-400 hover:border-brand-400 hover:text-brand-400'
                  }`}
                >
                  <Heart className={`h-6 w-6 ${isInWishlist ? 'fill-current' : ''}`} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAddToCart}
                  disabled={storeInfo?.site_lock === 1 || isAdding}
                  className="flex-1 bg-gradient-to-r from-brand-600 to-brand-500 hover:shadow-2xl text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 text-lg shadow-lg transition-all relative overflow-hidden"
                >
                  <motion.div
                    initial={false}
                    animate={showAddedToCart ? { y: -50 } : { y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShoppingCart className="h-6 w-6" />
                  </motion.div>
                  <motion.span
                    initial={false}
                    animate={showAddedToCart ? { y: -50 } : { y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {storeInfo?.site_lock === 1 ? 'Site Locked' : 'Add to Cart'}
                  </motion.span>
                  
                  {showAddedToCart && (
                    <motion.div
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 50, opacity: 0 }}
                      className="absolute inset-0 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-500"
                    >
                      <Check className="h-6 w-6" />
                      <span>Added!</span>
                    </motion.div>
                  )}
                </motion.button>
              </div>

              {/* Description Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="pt-4 space-y-4"
              >
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">About This Product</h3>
                  <p className="text-gray-600 leading-relaxed line-clamp-5">
                    {product.description || 'No description available'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* MORE INFO TABS */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-t border-gray-200">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Specifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Product Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Category</span>
                  <span className="text-gray-900 font-bold">{product.category || 'Hair Care'}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Product ID</span>
                  <span className="text-gray-900 font-bold">{product.id}</span>
                </div>
              </div>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us</h3>
              <div className="space-y-4">
                {[
                  'Authentic, quality fashion pieces, bags, shoes, accessories and more',
                  'Fast and reliable delivery to your doorstep',
                  'Secure payment processing with multiple options',
                  'Excellent customer service and support'
                ].map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* MOBILE BOTTOM ACTIONS */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-brand-100 z-40">
        <div className="grid grid-cols-2 gap-2 p-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleWishlist(product.id)}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-colors ${
              isInWishlist
                ? 'bg-brand-100 text-brand-600 border-2 border-brand-600'
                : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
            }`}
          >
            <Heart className={`h-5 w-5 ${isInWishlist ? 'fill-current' : ''}`} />
            Wishlist
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAddToCart}
            disabled={storeInfo?.site_lock === 1 || isAdding}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-brand-600 to-brand-500 text-white font-bold py-3 rounded-xl transition-all"
          >
            <ShoppingCart className="h-5 w-5" />
            {storeInfo?.site_lock === 1 ? 'Site Locked' : 'Add'}
          </motion.button>
        </div>
      </div>

      {/* Use shared AddedToCartModal for a prominent small modal on add-to-cart */}
      <AddedToCartModal
        open={showAddedToCart}
        onClose={() => setShowAddedToCart(false)}
        product={product ? {
          id: product.id,
          produt_name: product.produt_name,
          sellingprice: product.sellingprice,
          images: product.images || []
        } : { id: 0, produt_name: 'Product', sellingprice: '0', images: [] }}
        quantity={quantity}
        selectedVariations={selectedVariations.filter(v => v.quantity > 0)}
        onContinueShopping={() => setShowAddedToCart(false)}
        onGoToCart={() => { setShowAddedToCart(false); window.location.href = '/cart'; }}
      />
    </>
  );
}
